// ============================================
// Manor Miser - Shared Game Core
// ============================================
// Pure game logic + bot AI. No framework dependencies.
// Used by both server (GameEngine wrapper) and client (LocalGameManager wrapper).

import type {
  Player,
  PlayerPublic,
  BetResult,
  EliminationResult,
  GameOverResult,
  PlayerStats,
  GameStats,
  GameStatePublic,
  BetIncrement,
  GamePhase,
  RoundHistory,
} from '../types';
import {
  BETTING_DURATION,
  STARTING_MONEY,
  REVEAL_DURATION,
  ELIMINATION_DURATION,
  NEXT_ROUND_DELAY,
  BET_INCREMENTS,
} from '../constants';

// -----------------------------
// Bot Personality Types
// -----------------------------

export type BotStyle = 'aggressive' | 'normal' | 'cautious';

interface BotPersonality {
  style: BotStyle;
  budgetMultiplier: number;      // Scales base budget (>1 = overspend, <1 = underspend)
  budgetVariance: number;        // How much the budget randomly varies (0-1)
  incrementWeights: [number, number, number]; // Weights for [+1, +5, +10]
  reactiveProbability: number;   // Chance to react to human bets (0-1)
  betTimingSpread: number;       // How much of the window they use (0-1)
}

const BOT_PERSONALITIES: Record<BotStyle, BotPersonality> = {
  aggressive: {
    style: 'aggressive',
    budgetMultiplier: 1.4,       // Overspends — tries to win early
    budgetVariance: 0.5,         // High variance — unpredictable
    incrementWeights: [0.80, 0.15, 0.05], // Lots of +1s (many clicks, hard to read)
    reactiveProbability: 0.80,   // Very reactive to human bets
    betTimingSpread: 0.5,        // Bets early and fast
  },
  normal: {
    style: 'normal',
    budgetMultiplier: 1.0,       // Balanced budget
    budgetVariance: 0.3,         // Moderate variance
    incrementWeights: [0.60, 0.30, 0.10], // Balanced mix
    reactiveProbability: 0.55,   // Sometimes reacts
    betTimingSpread: 0.7,        // Spreads bets across the window
  },
  cautious: {
    style: 'cautious',
    budgetMultiplier: 0.65,      // Underspends — conserves for later rounds
    budgetVariance: 0.2,         // Low variance — consistent
    incrementWeights: [0.30, 0.40, 0.30], // Fewer, larger bets
    reactiveProbability: 0.30,   // Rarely reacts
    betTimingSpread: 0.9,        // Takes their time, bets late
  },
};

const BOT_STYLES: BotStyle[] = ['aggressive', 'normal', 'cautious'];

// -----------------------------
// Interfaces
// -----------------------------

export interface RoundStartedData {
  roundNumber: number;
  phaseEndsAt: number;
  yourMoney: number;
  yourBet: number;
  alivePlayers: PlayerPublic[];
}

export interface GameCoreCallbacks {
  onGameStartedForPlayer(playerId: string, gameState: GameStatePublic, yourMoney: number): void;
  onRoundStartedForPlayer(playerId: string, data: RoundStartedData): void;
  onBetProcessed(bettingPlayerId: string, isBot: boolean, newBet: number, remainingMoney: number): void;
  onRoundReveal(bets: BetResult[], phaseEndsAt: number): void;
  onPlayerEliminated(result: EliminationResult & { phaseEndsAt: number }): void;
  onGameOver(result: GameOverResult): void;
}

export interface TimerProvider {
  setTimeout(callback: () => void, ms: number): number;
  clearTimeout(id: number): void;
}

// -----------------------------
// GameCore Class
// -----------------------------

export class GameCore {
  private callbacks: GameCoreCallbacks;
  private timer: TimerProvider;

  // Player state
  private players: Map<string, Player>;
  private playerMoney: Map<string, number> = new Map();
  private playerBets: Map<string, number> = new Map();

  // Game identity
  private botIds: string[];
  private hostId: string;

  // Game state
  private phase: GamePhase = 'lobby';
  private roundNumber: number = 0;
  private eliminationOrder: string[] = [];

  // Timers
  private phaseTimer: number | null = null;
  private botTimers: number[] = [];

  // Stats
  private totalBetsPerPlayer: Map<string, number> = new Map();
  private biggestBetPerPlayer: Map<string, number> = new Map();
  private biggestPot: number = 0;
  private roundHistory: RoundHistory[] = [];

  // Bot strategy state
  private botBudgetTargets: Map<string, number> = new Map();
  private botPersonalities: Map<string, BotPersonality> = new Map();

  constructor(
    players: Map<string, Player>,
    botIds: string[],
    hostId: string,
    callbacks: GameCoreCallbacks,
    timer: TimerProvider
  ) {
    this.players = players;
    this.botIds = botIds;
    this.hostId = hostId;
    this.callbacks = callbacks;
    this.timer = timer;

    // Assign random personalities to bots
    for (const botId of botIds) {
      const style = BOT_STYLES[Math.floor(Math.random() * BOT_STYLES.length)];
      this.botPersonalities.set(botId, BOT_PERSONALITIES[style]);
    }
  }

  // -----------------------------
  // Public API
  // -----------------------------

  getPhase(): GamePhase {
    return this.phase;
  }

  getPlayerMoney(playerId: string): number {
    return this.playerMoney.get(playerId) ?? 0;
  }

  getPlayerBet(playerId: string): number {
    return this.playerBets.get(playerId) ?? 0;
  }

  // -----------------------------
  // Game Flow
  // -----------------------------

  startGame(): void {
    // Clean slate: clear any lingering timers from a previous game
    this.clearAllTimers();
    this.phase = 'lobby';

    // Initialize money and stats
    for (const [id, player] of this.players) {
      this.playerMoney.set(id, STARTING_MONEY);
      this.playerBets.set(id, 0);
      player.isAlive = true;
      this.totalBetsPerPlayer.set(id, 0);
      this.biggestBetPerPlayer.set(id, 0);
    }

    this.eliminationOrder = [];
    this.roundNumber = 0;
    this.biggestPot = 0;
    this.roundHistory = [];
    this.botBudgetTargets.clear();

    // Re-assign random personalities each game
    for (const botId of this.botIds) {
      const style = BOT_STYLES[Math.floor(Math.random() * BOT_STYLES.length)];
      this.botPersonalities.set(botId, BOT_PERSONALITIES[style]);
    }

    const publicPlayers = this.getAlivePublicPlayers();

    const gameState: GameStatePublic = {
      roomCode: 'LOCAL',
      phase: 'betting',
      roundNumber: 1,
      players: publicPlayers,
      hostId: this.hostId,
      settings: {
        startingMoney: STARTING_MONEY,
        bettingDuration: BETTING_DURATION,
        minPlayers: 2,
        maxPlayers: 8,
      },
      phaseEndsAt: null,
      eliminationOrder: [],
    };

    // Notify each player
    for (const [id] of this.players) {
      this.callbacks.onGameStartedForPlayer(
        id,
        gameState,
        this.playerMoney.get(id) ?? STARTING_MONEY
      );
    }

    // Delay before first round — must be longer than the scene transition
    // time (300ms fade + scene create) to ensure GameScene callbacks are ready
    this.phaseTimer = this.timer.setTimeout(() => {
      this.startBettingPhase();
    }, 1000);
  }

  handleAddBet(playerId: string, amount: BetIncrement): boolean {
    if (this.phase !== 'betting') return false;
    if (!(BET_INCREMENTS as readonly number[]).includes(amount)) return false;

    const player = this.players.get(playerId);
    if (!player || !player.isAlive) return false;

    const currentMoney = this.playerMoney.get(playerId) ?? 0;
    if (currentMoney < amount) return false;

    // Execute bet
    const currentBet = this.playerBets.get(playerId) ?? 0;
    const newBet = currentBet + amount;
    const newMoney = currentMoney - amount;
    this.playerBets.set(playerId, newBet);
    this.playerMoney.set(playerId, newMoney);

    const isBot = player.isBot === true;
    this.callbacks.onBetProcessed(playerId, isBot, newBet, newMoney);

    // If a non-bot player bet, trigger reactive bot betting
    if (!isBot) {
      this.onHumanBetActivity();
    }

    return true;
  }

  resetGame(): void {
    // startGame() handles its own timer cleanup and state reset
    this.startGame();
  }

  destroy(): void {
    this.clearAllTimers();
  }

  // -----------------------------
  // Phase State Machine
  // -----------------------------

  private startBettingPhase(): void {
    this.phase = 'betting';
    this.roundNumber++;

    // Reset bets
    for (const id of this.players.keys()) {
      this.playerBets.set(id, 0);
    }

    const phaseEndsAt = Date.now() + BETTING_DURATION * 1000;
    const alivePlayers = this.getAlivePlayers();
    const publicPlayers = alivePlayers.map(p => this.toPublicPlayer(p));

    // Notify each player
    for (const player of this.players.values()) {
      this.callbacks.onRoundStartedForPlayer(player.id, {
        roundNumber: this.roundNumber,
        phaseEndsAt,
        yourMoney: this.playerMoney.get(player.id) ?? 0,
        yourBet: 0,
        alivePlayers: publicPlayers,
      });
    }

    // Start bot betting
    this.scheduleBotBetting(alivePlayers.length);

    // Schedule end of betting
    this.clearPhaseTimer();
    this.phaseTimer = this.timer.setTimeout(() => {
      this.endBettingPhase();
    }, BETTING_DURATION * 1000);
  }

  private endBettingPhase(): void {
    this.clearPhaseTimer();
    this.stopBotBetting();

    const alivePlayers = this.getAlivePlayers();
    const bets: BetResult[] = alivePlayers.map(player => ({
      playerId: player.id,
      playerName: player.name,
      amount: this.playerBets.get(player.id) ?? 0,
    }));

    // Track stats
    let roundPot = 0;
    for (const bet of bets) {
      roundPot += bet.amount;
      const total = (this.totalBetsPerPlayer.get(bet.playerId) ?? 0) + bet.amount;
      this.totalBetsPerPlayer.set(bet.playerId, total);

      const biggest = this.biggestBetPerPlayer.get(bet.playerId) ?? 0;
      if (bet.amount > biggest) {
        this.biggestBetPerPlayer.set(bet.playerId, bet.amount);
      }
    }
    if (roundPot > this.biggestPot) {
      this.biggestPot = roundPot;
    }

    // Record round history (eliminatedPlayerId filled in during elimination phase)
    this.roundHistory.push({
      roundNumber: this.roundNumber,
      bets: bets.map(b => ({ playerId: b.playerId, playerName: b.playerName, amount: b.amount })),
      eliminatedPlayerId: null,
    });

    this.startRevealPhase(bets);
  }

  private startRevealPhase(bets: BetResult[]): void {
    this.phase = 'reveal';
    const phaseEndsAt = Date.now() + REVEAL_DURATION;

    this.callbacks.onRoundReveal(bets, phaseEndsAt);

    this.phaseTimer = this.timer.setTimeout(() => {
      this.startEliminationPhase(bets);
    }, REVEAL_DURATION);
  }

  private startEliminationPhase(bets: BetResult[]): void {
    this.clearPhaseTimer();
    this.phase = 'elimination';

    // Find lowest bet
    const minBet = Math.min(...bets.map(b => b.amount));
    const lowestBidders = bets.filter(b => b.amount === minBet);

    // Random tie-break
    const eliminated = lowestBidders[Math.floor(Math.random() * lowestBidders.length)];

    // Mark eliminated
    const eliminatedPlayer = this.players.get(eliminated.playerId);
    if (eliminatedPlayer) {
      eliminatedPlayer.isAlive = false;
      this.eliminationOrder.push(eliminatedPlayer.id);
    }

    // Update the current round's history with the eliminated player
    const currentRound = this.roundHistory[this.roundHistory.length - 1];
    if (currentRound) {
      currentRound.eliminatedPlayerId = eliminated.playerId;
    }

    const eliminationResult: EliminationResult = {
      eliminatedPlayerId: eliminated.playerId,
      eliminatedPlayerName: eliminated.playerName,
      eliminatedBet: eliminated.amount,
      wasTieBreaker: lowestBidders.length > 1,
      tiedPlayerIds: lowestBidders.length > 1
        ? lowestBidders.map(b => b.playerId)
        : undefined,
    };

    const phaseEndsAt = Date.now() + ELIMINATION_DURATION;

    this.callbacks.onPlayerEliminated({
      ...eliminationResult,
      phaseEndsAt,
    });

    const remainingAlive = this.getAlivePlayers();

    this.phaseTimer = this.timer.setTimeout(() => {
      if (remainingAlive.length <= 1) {
        this.endGame(remainingAlive[0] || null);
      } else {
        this.phaseTimer = this.timer.setTimeout(() => {
          this.startBettingPhase();
        }, NEXT_ROUND_DELAY);
      }
    }, ELIMINATION_DURATION);
  }

  private endGame(winner: Player | null): void {
    this.clearAllTimers();
    this.phase = 'gameOver';

    const playerStats: PlayerStats[] = [];

    if (winner) {
      playerStats.push({
        playerId: winner.id,
        playerName: winner.name,
        placement: 1,
        roundsSurvived: this.roundNumber,
        totalBet: this.totalBetsPerPlayer.get(winner.id) ?? 0,
        biggestBet: this.biggestBetPerPlayer.get(winner.id) ?? 0,
      });
    }

    for (let i = this.eliminationOrder.length - 1; i >= 0; i--) {
      const playerId = this.eliminationOrder[i];
      const player = this.players.get(playerId);
      if (player) {
        playerStats.push({
          playerId: player.id,
          playerName: player.name,
          placement: playerStats.length + 1,
          roundsSurvived: this.roundNumber - (this.eliminationOrder.length - i),
          totalBet: this.totalBetsPerPlayer.get(player.id) ?? 0,
          biggestBet: this.biggestBetPerPlayer.get(player.id) ?? 0,
        });
      }
    }

    const gameStats: GameStats = {
      totalRounds: this.roundNumber,
      biggestPot: this.biggestPot,
      playerStats,
    };

    const result: GameOverResult = {
      winnerId: winner?.id ?? '',
      winnerName: winner?.name ?? 'No winner',
      winnerFinalMoney: winner ? (this.playerMoney.get(winner.id) ?? 0) : 0,
      finalStandings: playerStats,
      stats: gameStats,
      roundHistory: this.roundHistory,
    };

    this.callbacks.onGameOver(result);
  }

  // -----------------------------
  // Bot AI
  // -----------------------------

  private scheduleBotBetting(alivePlayerCount: number): void {
    this.stopBotBetting();
    this.botBudgetTargets.clear();

    const aliveBotIds = this.getAliveBotIds();

    for (const botId of aliveBotIds) {
      const personality = this.botPersonalities.get(botId) ?? BOT_PERSONALITIES.normal;
      const target = this.calculateBudgetTarget(botId, alivePlayerCount, personality);
      this.botBudgetTargets.set(botId, target);

      const actions = this.decomposeBetActions(target, personality);
      const windowMs = BETTING_DURATION * 1000 * personality.betTimingSpread;

      for (const amount of actions) {
        const delay = Math.random() * windowMs + 500;
        const timer = this.timer.setTimeout(() => {
          this.tryExecuteBotBet(botId, amount);
        }, delay);
        this.botTimers.push(timer);
      }
    }
  }

  private onHumanBetActivity(): void {
    const aliveBotIds = this.getAliveBotIds();
    for (const botId of aliveBotIds) {
      this.scheduleReactiveBet(botId);
    }
  }

  private scheduleReactiveBet(botId: string): void {
    const personality = this.botPersonalities.get(botId) ?? BOT_PERSONALITIES.normal;

    if (Math.random() > personality.reactiveProbability) return;

    const currentBet = this.playerBets.get(botId) ?? 0;
    const target = this.botBudgetTargets.get(botId) ?? 0;

    if (currentBet >= target * 1.2) return;

    const delay = 1000 + Math.random() * 2000;
    const amount = this.pickBetIncrement(personality);

    const timer = this.timer.setTimeout(() => {
      this.tryExecuteBotBet(botId, amount);
    }, delay);
    this.botTimers.push(timer);
  }

  private tryExecuteBotBet(botId: string, amount: BetIncrement): void {
    if (this.phase !== 'betting') return;

    const money = this.playerMoney.get(botId) ?? 0;
    if (money < amount) {
      if (money >= 1) {
        this.handleAddBet(botId, 1);
      }
      return;
    }

    this.handleAddBet(botId, amount);
  }

  private calculateBudgetTarget(
    botId: string,
    alivePlayerCount: number,
    personality: BotPersonality
  ): number {
    const money = this.playerMoney.get(botId) ?? 0;
    const remainingRounds = Math.max(alivePlayerCount - 1, 1);
    const baseBudget = (money / remainingRounds) * personality.budgetMultiplier;

    // Apply personality-specific variance
    const v = personality.budgetVariance;
    const variance = 1 + (Math.random() * v * 2 - v);
    const target = Math.round(baseBudget * variance);

    return Math.max(0, Math.min(target, money));
  }

  private decomposeBetActions(target: number, _personality: BotPersonality): BetIncrement[] {
    // Greedy decomposition: use largest increments first to minimize bet actions
    const actions: BetIncrement[] = [];
    let remaining = target;

    while (remaining >= 10) {
      actions.push(10);
      remaining -= 10;
    }
    while (remaining >= 5) {
      actions.push(5);
      remaining -= 5;
    }
    while (remaining >= 1) {
      actions.push(1);
      remaining -= 1;
    }

    return actions;
  }

  private pickBetIncrement(personality: BotPersonality): BetIncrement {
    const [w1, w5, w10] = personality.incrementWeights;
    const roll = Math.random();
    if (roll < w1) return 1;
    if (roll < w1 + w5) return 5;
    return 10;
  }

  private stopBotBetting(): void {
    for (const timer of this.botTimers) {
      this.timer.clearTimeout(timer);
    }
    this.botTimers = [];
  }

  // -----------------------------
  // Helpers
  // -----------------------------

  private getAlivePlayers(): Player[] {
    return Array.from(this.players.values()).filter(p => p.isAlive);
  }

  private getAliveBotIds(): string[] {
    return this.botIds.filter(id => {
      const player = this.players.get(id);
      return player && player.isAlive;
    });
  }

  private getAlivePublicPlayers(): PlayerPublic[] {
    return this.getAlivePlayers().map(p => this.toPublicPlayer(p));
  }

  private toPublicPlayer(player: Player): PlayerPublic {
    return {
      id: player.id,
      name: player.name,
      isAlive: player.isAlive,
      isConnected: player.isConnected,
      isHost: player.isHost,
      isBot: player.isBot,
      characterType: player.characterType,
    };
  }

  private clearPhaseTimer(): void {
    if (this.phaseTimer !== null) {
      this.timer.clearTimeout(this.phaseTimer);
      this.phaseTimer = null;
    }
  }

  private clearAllTimers(): void {
    this.clearPhaseTimer();
    this.stopBotBetting();
  }
}
